import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-shell-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, NgClass],
  templateUrl: './shell-sidebar.component.html',
  styleUrl: './shell-sidebar.component.scss'
})
export class ShellSidebarComponent implements OnChanges, OnDestroy {
  @Input() collapsed = false;
  @Input() items: MenuItem[] = [];
  @Input() openState: Record<string, boolean> = {};

  @Output() toggleSectionEvent = new EventEmitter<string>();

  activeFlyoutId: string | null = null;

  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collapsed'] && !this.collapsed) {
      this.closeFlyout();
    }
  }

  ngOnDestroy(): void {
    this.clearHoverCloseTimer();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.collapsed || !this.activeFlyoutId) {
      return;
    }

    const target = event.target as Node | null;

    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.closeFlyout();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeFlyout();
  }

  onNavItemHover(item: MenuItem): void {
    if (!this.collapsed || !this.hasChildren(item) || !item.id) {
      return;
    }

    this.clearHoverCloseTimer();
    this.activeFlyoutId = item.id;
  }

  onNavItemHoverEnd(): void {
    if (!this.collapsed) {
      return;
    }

    this.scheduleHoverClose();
  }

  onFlyoutHover(): void {
    this.clearHoverCloseTimer();
  }

  toggleParent(item: MenuItem, event: Event): void {
    event.stopPropagation();

    const id = item.id;

    if (!id) {
      return;
    }

    if (this.collapsed && this.hasChildren(item)) {
      this.clearHoverCloseTimer();
      this.activeFlyoutId = this.activeFlyoutId === id ? null : id;
      return;
    }

    if (this.collapsed) {
      return;
    }

    this.toggleSectionEvent.emit(id);
  }

  closeFlyout(): void {
    this.clearHoverCloseTimer();
    this.activeFlyoutId = null;
  }

  onSubmenuNavigate(): void {
    if (this.collapsed) {
      this.closeFlyout();
    }
  }

  isExpandedSubmenuOpen(item: MenuItem): boolean {
    if (!item?.id || !item.items?.length || this.collapsed) {
      return false;
    }

    return !!this.openState[item.id];
  }

  isFlyoutOpen(item: MenuItem): boolean {
    if (!this.collapsed || !item?.id || !item.items?.length) {
      return false;
    }

    return this.activeFlyoutId === item.id;
  }

  hasChildren(item: MenuItem): boolean {
    return !!item?.items?.length;
  }

  trackById = (index: number, item: MenuItem): string => {
    return item.id ?? item.label ?? String(index);
  };

  private scheduleHoverClose(): void {
    this.clearHoverCloseTimer();
    this.hoverCloseTimer = setTimeout(() => {
      this.activeFlyoutId = null;
      this.hoverCloseTimer = null;
    }, 200);
  }

  private clearHoverCloseTimer(): void {
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }
}
